import React, { useState, useEffect } from 'react';
import { interviewsApi, applicationsApi, calendarApi, getErrorMessage } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus,
  Search,
  MoreVertical,
  Calendar as CalendarIcon,
  Clock,
  Video,
  User,
  Loader2,
  CheckCircle,
  XCircle,
  Pause,
  MessageSquare,
  CalendarPlus,
  CalendarX,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, isToday, isTomorrow, isPast, isFuture, startOfDay, endOfDay, addDays } from 'date-fns';

const resultColors = {
  pass: 'bg-green-100 text-green-700',
  fail: 'bg-red-100 text-red-600',
  on_hold: 'bg-amber-100 text-amber-700',
};

const Interviews = () => {
  const { user } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [syncToCalendar, setSyncToCalendar] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [syncingCalendar, setSyncingCalendar] = useState(null);
  const [applicationSearch, setApplicationSearch] = useState('');
  const [formData, setFormData] = useState({
    application_id: '',
    interview_type: 'technical',
    scheduled_at: '',
    duration_minutes: 60,
    interviewer_name: '',
    interviewer_email: '',
    meeting_link: '',
    notes: '',
  });
  const [feedbackData, setFeedbackData] = useState({
    feedback: '',
    result: '',
  });

  // Filter applications by search term
  const filteredApplications = applications.filter(app => {
    if (!applicationSearch) return true;
    const search = applicationSearch.toLowerCase();
    const candidateName = (app.candidate_name || '').toLowerCase();
    const jobTitle = (app.job_title || '').toLowerCase();
    return candidateName.includes(search) || jobTitle.includes(search);
  });

  useEffect(() => {
    fetchData();
    checkCalendarStatus();
  }, []);

  const checkCalendarStatus = async () => {
    try {
      const response = await calendarApi.getStatus();
      setCalendarConnected(response.data.connected);
    } catch (error) {
      setCalendarConnected(false);
    }
  };

  const fetchData = async () => {
    try {
      const [interviewsRes, appsRes] = await Promise.all([
        interviewsApi.getAll(),
        applicationsApi.getAll({ page_size: 1000 }), // Fetch more applications for search
      ]);
      
      // Handle paginated response for interviews
      const interviewsData = interviewsRes.data;
      setInterviews(interviewsData.items || (Array.isArray(interviewsData) ? interviewsData : []));
      
      // Handle paginated response for applications
      const appsData = appsRes.data;
      const allApps = appsData.items || (Array.isArray(appsData) ? appsData : []);
      
      // Filter applications that can be scheduled for interviews
      // Include all stages that might need interview scheduling
      const interviewableStages = ['sourced', 'screening', 'client_submission', 'interview_scheduled', 'interview_selected'];
      const interviewApps = allApps.filter(
        (a) => interviewableStages.includes(a.stage)
      );
      setApplications(interviewApps);
    } catch (error) {
      toast.error('Failed to load interviews');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setFormLoading(true);
    try {
      const response = await interviewsApi.create(formData);
      const newInterview = response.data;
      toast.success('Interview scheduled successfully');
      
      // Sync to Google Calendar if connected and user wants to
      if (calendarConnected && syncToCalendar) {
        try {
          const calResponse = await interviewsApi.createCalendarEvent(newInterview.id, []);
          toast.success('Calendar event created!');
        } catch (calError) {
          toast.warning('Interview created but calendar sync failed');
        }
      }
      
      setIsCreateOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to schedule interview'));
    } finally {
      setFormLoading(false);
    }
  };

  const handleFeedback = async () => {
    setFormLoading(true);
    try {
      await interviewsApi.submitFeedback(selectedInterview.id, feedbackData);
      toast.success('Feedback submitted successfully');
      setIsFeedbackOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit feedback'));
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      application_id: '',
      interview_type: 'technical',
      scheduled_at: '',
      duration_minutes: 60,
      interviewer_name: '',
      interviewer_email: '',
      meeting_link: '',
      notes: '',
    });
    setApplicationSearch(''); // Reset search when form is reset
  };

  const handleSyncToCalendar = async (interview) => {
    if (!calendarConnected) {
      toast.error('Please connect your Google Calendar in Settings first');
      return;
    }
    
    setSyncingCalendar(interview.id);
    try {
      const response = await interviewsApi.createCalendarEvent(interview.id, []);
      toast.success('Interview synced to Google Calendar!');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to sync to calendar'));
    } finally {
      setSyncingCalendar(null);
    }
  };

  const handleRemoveFromCalendar = async (interview) => {
    if (!window.confirm('Remove this interview from Google Calendar?')) return;
    
    setSyncingCalendar(interview.id);
    try {
      await interviewsApi.deleteCalendarEvent(interview.id);
      toast.success('Removed from Google Calendar');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove from calendar'));
    } finally {
      setSyncingCalendar(null);
    }
  };

  const openFeedback = (interview) => {
    setSelectedInterview(interview);
    setFeedbackData({ feedback: '', result: '' });
    setIsFeedbackOpen(true);
  };

  const openView = (interview) => {
    setSelectedInterview(interview);
    setIsViewOpen(true);
  };

  const getInterviewsForDate = (date) => {
    return interviews.filter((i) => {
      const interviewDate = new Date(i.scheduled_at);
      return (
        interviewDate >= startOfDay(date) && 
        interviewDate <= endOfDay(date)
      );
    });
  };

  const getTodayInterviews = () => getInterviewsForDate(new Date());
  const getUpcomingInterviews = () => interviews.filter((i) => isFuture(new Date(i.scheduled_at)));
  const getPastInterviews = () => interviews.filter((i) => isPast(new Date(i.scheduled_at)));

  const interviewDates = interviews.map((i) => startOfDay(new Date(i.scheduled_at)));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="interviews-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Interviews
          </h1>
          <p className="text-slate-600 mt-1">Schedule and manage interviews</p>
        </div>
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-teal-700 hover:bg-teal-800" data-testid="schedule-interview-btn">
              <Plus className="w-4 h-4 mr-2" /> Schedule Interview
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Schedule Interview</DialogTitle>
              <DialogDescription>Set up a new interview</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              <div className="space-y-2">
                <Label>Application *</Label>
                <div className="space-y-2">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search by candidate name or job title..."
                      value={applicationSearch}
                      onChange={(e) => setApplicationSearch(e.target.value)}
                      className="pl-9"
                      data-testid="application-search"
                    />
                  </div>
                  
                  {/* Application Select */}
                  {applications.length === 0 ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-700">
                        No applications available for interview scheduling. 
                        Move candidates to screening or interview stages first.
                      </p>
                    </div>
                  ) : filteredApplications.length === 0 ? (
                    <div className="p-3 bg-slate-50 border rounded-lg">
                      <p className="text-sm text-slate-500">
                        No applications match "{applicationSearch}"
                      </p>
                    </div>
                  ) : (
                    <Select
                      value={formData.application_id}
                      onValueChange={(v) => setFormData({ ...formData, application_id: v })}
                    >
                      <SelectTrigger data-testid="interview-application-select">
                        <SelectValue placeholder="Select candidate application" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[200px]">
                        {filteredApplications.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{a.candidate_name || 'Unknown Candidate'}</span>
                              <span className="text-xs text-slate-500">{a.job_title || 'No Job'} • {a.stage}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {/* Show count */}
                  <p className="text-xs text-slate-500">
                    {filteredApplications.length} of {applications.length} applications
                    {applicationSearch && ` matching "${applicationSearch}"`}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Interview Type</Label>
                  <Select
                    value={formData.interview_type}
                    onValueChange={(v) => setFormData({ ...formData, interview_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="hr">HR</SelectItem>
                      <SelectItem value="managerial">Managerial</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                      <SelectItem value="final">Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                    min={15}
                    step={15}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) => setFormData({ ...formData, scheduled_at: e.target.value })}
                  data-testid="interview-datetime"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Interviewer Name</Label>
                  <Input
                    value={formData.interviewer_name}
                    onChange={(e) => setFormData({ ...formData, interviewer_name: e.target.value })}
                    placeholder="Interviewer name"
                    data-testid="interviewer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Interviewer Email</Label>
                  <Input
                    type="email"
                    value={formData.interviewer_email}
                    onChange={(e) => setFormData({ ...formData, interviewer_email: e.target.value })}
                    placeholder="interviewer@company.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Meeting Link</Label>
                <Input
                  value={formData.meeting_link}
                  onChange={(e) => setFormData({ ...formData, meeting_link: e.target.value })}
                  placeholder="https://meet.google.com/..."
                  data-testid="meeting-link"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Interview notes..."
                  rows={2}
                />
              </div>

              {/* Google Calendar Sync Option */}
              {calendarConnected && (
                <div className="flex items-center space-x-2 p-3 bg-teal-50 rounded-lg">
                  <Checkbox
                    id="sync-calendar"
                    checked={syncToCalendar}
                    onCheckedChange={setSyncToCalendar}
                  />
                  <label
                    htmlFor="sync-calendar"
                    className="text-sm font-medium text-teal-800 cursor-pointer flex items-center gap-2"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    Create Google Calendar event
                  </label>
                </div>
              )}
              {!calendarConnected && (
                <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-500">
                  <CalendarIcon className="w-4 h-4 inline mr-2" />
                  Connect Google Calendar in Settings to auto-sync interviews
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={handleCreate}
                disabled={formLoading || !formData.application_id || !formData.scheduled_at}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="submit-interview-btn"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Schedule Interview
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Calendar</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border"
              modifiers={{
                hasInterview: interviewDates,
              }}
              modifiersStyles={{
                hasInterview: { fontWeight: 'bold', color: '#0F766E' },
              }}
              data-testid="interview-calendar"
            />
            
            {/* Selected Date Interviews */}
            <div className="mt-4">
              <h4 className="font-medium text-slate-900 mb-2">
                {format(selectedDate, 'MMMM dd, yyyy')}
              </h4>
              <div className="space-y-2">
                {getInterviewsForDate(selectedDate).length === 0 ? (
                  <p className="text-sm text-slate-500">No interviews scheduled</p>
                ) : (
                  getInterviewsForDate(selectedDate).map((interview) => (
                    <div
                      key={interview.id}
                      className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100"
                      onClick={() => openView(interview)}
                    >
                      <p className="font-medium text-sm text-slate-900">{interview.candidate_name}</p>
                      <p className="text-xs text-slate-500">
                        {format(new Date(interview.scheduled_at), 'hh:mm a')} - {interview.interview_type}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Interviews List */}
        <Card className="lg:col-span-2">
          <CardContent className="p-0">
            <Tabs defaultValue="upcoming" className="w-full">
              <div className="border-b px-4">
                <TabsList className="grid w-full grid-cols-3 max-w-md">
                  <TabsTrigger value="today">Today ({getTodayInterviews().length})</TabsTrigger>
                  <TabsTrigger value="upcoming">Upcoming ({getUpcomingInterviews().length})</TabsTrigger>
                  <TabsTrigger value="past">Past ({getPastInterviews().length})</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="today" className="m-0">
                <InterviewTable 
                  interviews={getTodayInterviews()} 
                  onView={openView}
                  onFeedback={openFeedback}
                />
              </TabsContent>

              <TabsContent value="upcoming" className="m-0">
                <InterviewTable 
                  interviews={getUpcomingInterviews()} 
                  onView={openView}
                  onFeedback={openFeedback}
                />
              </TabsContent>

              <TabsContent value="past" className="m-0">
                <InterviewTable 
                  interviews={getPastInterviews()} 
                  onView={openView}
                  onFeedback={openFeedback}
                  showResult
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Feedback Dialog */}
      <Dialog open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Feedback</DialogTitle>
            <DialogDescription>
              Provide feedback for {selectedInterview?.candidate_name}'s interview
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Result *</Label>
              <Select
                value={feedbackData.result}
                onValueChange={(v) => setFeedbackData({ ...feedbackData, result: v })}
              >
                <SelectTrigger data-testid="feedback-result">
                  <SelectValue placeholder="Select result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pass">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" /> Pass
                    </div>
                  </SelectItem>
                  <SelectItem value="fail">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" /> Fail
                    </div>
                  </SelectItem>
                  <SelectItem value="on_hold">
                    <div className="flex items-center gap-2">
                      <Pause className="w-4 h-4 text-amber-600" /> On Hold
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Feedback *</Label>
              <Textarea
                value={feedbackData.feedback}
                onChange={(e) => setFeedbackData({ ...feedbackData, feedback: e.target.value })}
                placeholder="Detailed feedback about the interview..."
                rows={4}
                data-testid="feedback-text"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleFeedback}
              disabled={formLoading || !feedbackData.result || !feedbackData.feedback}
              className="bg-teal-700 hover:bg-teal-800"
              data-testid="submit-feedback-btn"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Feedback
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Interview Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Interview Details</DialogTitle>
          </DialogHeader>
          {selectedInterview && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-teal-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{selectedInterview.candidate_name}</p>
                  <p className="text-sm text-slate-500">{selectedInterview.job_title}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-slate-600">
                  <CalendarIcon className="w-4 h-4" />
                  {format(new Date(selectedInterview.scheduled_at), 'MMM dd, yyyy')}
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Clock className="w-4 h-4" />
                  {format(new Date(selectedInterview.scheduled_at), 'hh:mm a')} ({selectedInterview.duration_minutes}m)
                </div>
              </div>

              <div className="flex gap-2">
                <Badge variant="outline" className="capitalize">
                  {selectedInterview.interview_type}
                </Badge>
                {selectedInterview.result && (
                  <Badge className={resultColors[selectedInterview.result]}>
                    {selectedInterview.result.replace('_', ' ')}
                  </Badge>
                )}
                {selectedInterview.google_event_id && (
                  <Badge className="bg-blue-100 text-blue-700">
                    <CalendarIcon className="w-3 h-3 mr-1" /> Synced
                  </Badge>
                )}
              </div>

              {selectedInterview.interviewer_name && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Interviewer</h4>
                  <p className="text-slate-600 text-sm">
                    {selectedInterview.interviewer_name}
                    {selectedInterview.interviewer_email && ` (${selectedInterview.interviewer_email})`}
                  </p>
                </div>
              )}

              {selectedInterview.meeting_link && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Meeting Link</h4>
                  <a
                    href={selectedInterview.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-700 hover:underline text-sm flex items-center gap-1"
                  >
                    <Video className="w-4 h-4" /> Join Meeting
                  </a>
                </div>
              )}

              {selectedInterview.notes && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Notes</h4>
                  <p className="text-slate-600 text-sm">{selectedInterview.notes}</p>
                </div>
              )}

              {selectedInterview.feedback && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-1">Feedback</h4>
                  <p className="text-slate-600 text-sm">{selectedInterview.feedback}</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                {!selectedInterview.result && isPast(new Date(selectedInterview.scheduled_at)) && (
                  <Button
                    onClick={() => { setIsViewOpen(false); openFeedback(selectedInterview); }}
                    className="flex-1 bg-teal-700 hover:bg-teal-800"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" /> Add Feedback
                  </Button>
                )}
                
                {/* Calendar Sync Buttons */}
                {calendarConnected && !selectedInterview.google_event_id && (
                  <Button
                    variant="outline"
                    onClick={() => handleSyncToCalendar(selectedInterview)}
                    disabled={syncingCalendar === selectedInterview.id}
                    data-testid="sync-to-calendar-btn"
                  >
                    {syncingCalendar === selectedInterview.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CalendarPlus className="w-4 h-4 mr-2" />
                    )}
                    Sync to Calendar
                  </Button>
                )}
                {selectedInterview.google_event_id && (
                  <Button
                    variant="outline"
                    onClick={() => handleRemoveFromCalendar(selectedInterview)}
                    disabled={syncingCalendar === selectedInterview.id}
                    className="text-red-600 hover:text-red-700"
                    data-testid="remove-from-calendar-btn"
                  >
                    {syncingCalendar === selectedInterview.id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CalendarX className="w-4 h-4 mr-2" />
                    )}
                    Remove from Calendar
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const InterviewTable = ({ interviews, onView, onFeedback, showResult = false }) => (
  <Table
    containerClassName="max-h-[calc(100vh-300px)] min-h-[320px]"
    data-testid="interviews-table"
  >
    <TableHeader className="sticky top-0 z-10 bg-white shadow-sm">
      <TableRow>
        <TableHead>Candidate</TableHead>
        <TableHead>Type</TableHead>
        <TableHead>Date & Time</TableHead>
        <TableHead>Interviewer</TableHead>
        {showResult && <TableHead>Result</TableHead>}
        <TableHead className="w-[50px]"></TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {interviews.length === 0 ? (
        <TableRow>
          <TableCell colSpan={showResult ? 6 : 5} className="text-center py-8 text-slate-500">
            No interviews found
          </TableCell>
        </TableRow>
      ) : (
        interviews.map((interview) => (
          <TableRow key={interview.id} className="table-row-hover">
            <TableCell>
              <div>
                <p className="font-medium text-slate-900">{interview.candidate_name}</p>
                <p className="text-sm text-slate-500">{interview.job_title}</p>
              </div>
            </TableCell>
            <TableCell>
              <Badge variant="outline" className="capitalize">
                {interview.interview_type}
              </Badge>
            </TableCell>
            <TableCell>
              <div>
                <p className="font-medium text-slate-900">
                  {format(new Date(interview.scheduled_at), 'MMM dd, yyyy')}
                </p>
                <p className="text-sm text-slate-500">
                  {format(new Date(interview.scheduled_at), 'hh:mm a')}
                </p>
              </div>
            </TableCell>
            <TableCell>{interview.interviewer_name || '-'}</TableCell>
            {showResult && (
              <TableCell>
                {interview.result ? (
                  <Badge className={resultColors[interview.result]}>
                    {interview.result.replace('_', ' ')}
                  </Badge>
                ) : (
                  <Badge variant="outline">Pending</Badge>
                )}
              </TableCell>
            )}
            <TableCell>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onView(interview)}>
                    View Details
                  </DropdownMenuItem>
                  {!interview.result && isPast(new Date(interview.scheduled_at)) && (
                    <DropdownMenuItem onClick={() => onFeedback(interview)}>
                      Add Feedback
                    </DropdownMenuItem>
                  )}
                  {interview.meeting_link && (
                    <DropdownMenuItem asChild>
                      <a href={interview.meeting_link} target="_blank" rel="noopener noreferrer">
                        Join Meeting
                      </a>
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))
      )}
    </TableBody>
  </Table>
);

export default Interviews;
